import { useCallback, useEffect, useMemo, useState } from 'react'
import { AgGridProvider, AgGridReact } from 'ag-grid-react' // the AG Grid React Component
import styles from './TraceDataTableAGGrid.module.css'
import type TraceEvent from '../tracer-data/TraceEvent'
import { launchTraceEventDetailView } from './TraceEventViewFunctions'
import { launchTraceEventPageView } from './TraceEventViewFunctions'
import type TraceLogDataAccess from '../tracer-data/TraceLogDataAccess'
import {
  type Module,
  themeBalham,
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowStyleModule,
  ValidationModule,
  NumberFilterModule,
  TextFilterModule,
  type CellClassParams,
  type CellClassRules,
  type CellClickedEvent,
  type GetRowIdParams,
  type GridReadyEvent,
  type RowClassParams,
  type ColDef,
} from 'ag-grid-community'

const AGGridModules: Module[] = [
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowStyleModule,
  NumberFilterModule,
  TextFilterModule,
]
if (import.meta.env.DEV) {
  AGGridModules.push(ValidationModule)
}

const eventNameClassRules: CellClassRules = {}
eventNameClassRules[styles.accessDenied] = (params: CellClassParams<TraceEvent>) => params.value === 'Access Denied'

interface TraceDataTableProps {
  traceLogDataAccessor: TraceLogDataAccess
  traceLogKey: number
}

export default function TraceDataTableAGGrid(props: TraceDataTableProps) {
  const [data, setData] = useState<TraceEvent[]>([])
  useEffect(() => {
    void props.traceLogDataAccessor.getTraceEvents(props.traceLogKey).then(setData)
  }, [props.traceLogDataAccessor, props.traceLogKey])

  const defaultColDef: ColDef<TraceEvent> = useMemo(() => {
    return {
      sortable: false,
    }
  }, [])

  const columns: ColDef<TraceEvent>[] = useMemo(
    () => [
      {
        colId: 'sequence',
        headerName: 'Line',
        field: 'sequenceDisplay',
        minWidth: 20,
        maxWidth: 45,
        cellClass: styles.firstCell,
      },
      {
        colId: 'threadName',
        headerName: 'Thread',
        field: 'threadName',
        filter: 'agTextColumnFilter',
        minWidth: 75,
        flex: 1,
      },
      { colId: 'interaction', headerName: 'Int', field: 'interaction', minWidth: 20, maxWidth: 30 },
      { colId: 'activityNmber', headerName: 'Rule#', field: 'activityNumber', minWidth: 25, maxWidth: 50 },
      { colId: 'stepMethod', headerName: 'Step Method', field: 'stepMethodDisplay', minWidth: 100, flex: 2 },
      {
        colId: 'primaryPageName',
        headerName: 'Step Page',
        field: 'primaryPageNameDisplay',
        filter: 'agTextColumnFilter',
        minWidth: 100,
        flex: 3,
      },
      {
        colId: 'stepNumber',
        headerName: 'Step',
        field: 'stepNumber',
        minWidth: 25,
        maxWidth: 35,
        cellClass: styles.stepNumber,
      },
      {
        colId: 'status',
        headerName: 'Status',
        field: 'statusDisplay',
        filter: 'agTextColumnFilter',
        minWidth: 75,
        maxWidth: 150,
      },
      {
        colId: 'eventName',
        headerName: 'Event Type',
        field: 'eventNameDisplay',
        filter: 'agTextColumnFilter',
        minWidth: 75,
        cellClassRules: eventNameClassRules,
      },
      { colId: 'elapsed', headerName: 'Elapsed', field: 'elapsedDisplay', minWidth: 35, maxWidth: 75 },
      { colId: 'name', headerName: 'Name', field: 'nameDisplay', filter: 'agTextColumnFilter', minWidth: 125, flex: 4 },
      {
        colId: 'ruleset',
        headerName: 'Ruleset',
        field: 'rulesetDisplay',
        filter: 'agTextColumnFilter',
        minWidth: 100,
        flex: 2,
        cellClass: styles.lastCell,
      },
    ],
    []
  )

  const getRowClass = useCallback((params: RowClassParams<TraceEvent>) => {
    const classNames = []
    const event = params.data
    if (event) {
      switch (event.eventType) {
        case 'Interaction':
        case 'Stream Rules':
          classNames.push(styles.interactionEvent)
          break
        case 'ADP Load':
          classNames.push(styles.adpEvent)
          break
      }
    }

    return classNames
  }, [])

  const cellClickHandler = useCallback((event: CellClickedEvent<TraceEvent>) => {
    const eventKey = event.data?.key
    if (event.colDef.colId === 'primaryPageName' && event.value !== '') {
      launchTraceEventPageView('primary', eventKey)
    } else {
      launchTraceEventDetailView(eventKey)
    }
  }, [])

  const getRowId = useCallback((param: GetRowIdParams<TraceEvent>) => `${param.data.key}`, [])

  const onGridReady = useCallback((event: GridReadyEvent<TraceEvent>) => {
    event.api.sizeColumnsToFit()
  }, [])

  return (
    <div className={styles.container}>
      <AgGridProvider modules={AGGridModules}>
        <AgGridReact<TraceEvent>
          theme={themeBalham}
          rowData={data}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          onCellClicked={cellClickHandler}
          getRowId={getRowId}
          getRowClass={getRowClass}
          onGridReady={onGridReady}
          enableBrowserTooltips={true}
          suppressHorizontalScroll={true}
        ></AgGridReact>
      </AgGridProvider>
    </div>
  )
}
